import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, LayersControl } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { propertyService } from '../../api/propertyService';
import { SnackBarComponent } from '../../Components/snackBar/snackBar';
import { LoadingSpinner } from '../../Components/spinner/spinner';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const { BaseLayer } = LayersControl;

const PropertyMap = () => {
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [filteredBySearch, setFilteredBySearch] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [userLocation, setUserLocation] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [selectedPropertyIndex, setSelectedPropertyIndex] = useState(null);
  const [showList, setShowList] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  
  // Snackbar states
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  // Refs
  const mapRef = useRef();
  const listRefs = useRef([]);
  const searchInputRef = useRef(null);

  // Property type configuration
  const propertyTypes = {
    // '3(2)': { label: "3(2)", color: "#3B82F6", icon: "3(2)" },
    // '5(2)': { label: "5(2)", color: "#EF4444", icon: "5(2)" },
    // 'valuation': { label: "Valuation", color: "#10B981", icon: "V" },
    'auction': { label: "Auction", color: "#8B5CF6", icon: "A" },
    // 'section13': { label: "Section 13", color: "#EC4899", icon: "S13" }
  };

  // Status configuration
  const statusConfig = {
    'Active': { color: '#10B981', label: 'Active' },
    'Inactive': { color: '#EF4444', label: 'Inactive' },
    'Pending': { color: '#F59E0B', label: 'Pending' },
    'Sold': { color: '#8B5CF6', label: 'Sold' },
    'Under Review': { color: '#3B82F6', label: 'Under Review' }
  };

  // Inline styles
  const styles = {
    container: {
      position: 'relative',
      marginTop: '60px',
      width: '100%',
      height: '100vh',
      fontFamily: 'Arial, sans-serif',
      backgroundColor: '#f3f4f6'
    },
    header: {
      position: 'absolute',
      top: '20px',
      left: '50%',
      transform: 'translateX(-50%)',
      background: 'white',
      padding: '15px 30px',
      borderRadius: '10px',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
      zIndex: 1000,
      textAlign: 'center',
      border: '1px solid #e5e7eb',
      maxWidth: '80%',
      width: 'auto'
    },
    headerTitle: {
      margin: '0 0 5px 0',
      fontSize: '20px',
      fontWeight: '700',
      color: '#1e40af'
    },
    headerSubtitle: {
      margin: 0,
      fontSize: '12px',
      color: '#6b7280'
    },
    filterPanel: {
      position: 'absolute',
      top: '120px',
      left: '20px',
      background: 'white',
      padding: '15px',
      borderRadius: '10px',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
      zIndex: 1000,
      minWidth: '250px',
      border: '1px solid #e5e7eb'
    },
    filterTitle: {
      margin: '0 0 12px 0',
      fontSize: '16px',
      fontWeight: '600',
      color: '#111827'
    },
    filterButtons: {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
      marginBottom: '12px'
    },
    filterButton: (isActive, color) => ({
      padding: '8px 12px',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontWeight: '500',
      fontSize: '13px',
      transition: 'all 0.2s ease',
      backgroundColor: isActive ? color || '#3B82F6' : '#f9fafb',
      color: isActive ? 'white' : '#4b5563',
      borderLeft: `4px solid ${isActive ? color || '#3B82F6' : '#d1d5db'}`,
      textAlign: 'left',
      '&:hover': {
        backgroundColor: isActive ? color || '#3B82F6' : '#f3f4f6'
      }
    }),
    legend: {
      marginTop: '15px',
      borderTop: '1px solid #e5e7eb',
      paddingTop: '12px'
    },
    legendTitle: {
      margin: '0 0 8px 0',
      fontSize: '13px',
      fontWeight: '600',
      color: '#4b5563'
    },
    legendItem: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '6px',
      fontSize: '12px',
      color: '#6b7280'
    },
    legendColor: (color) => ({
      width: '10px',
      height: '10px',
      borderRadius: '50%',
      backgroundColor: color,
      marginRight: '8px',
      flexShrink: 0
    }),
    mapContainer: {
      height: '100%',
      width: '100%',
      borderRadius: '8px'
    },
    // Search Bar Styles
    searchContainer: {
      position: 'absolute',
      top: '120px',
      right: '20px',
      background: 'white',
      borderRadius: '10px',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
      zIndex: 1000,
      minWidth: '300px',
      maxWidth: '400px',
      border: '1px solid #e5e7eb',
      overflow: 'hidden'
    },
    searchHeader: {
      padding: '15px',
      borderBottom: '1px solid #e5e7eb',
      background: '#f8fafc'
    },
    searchTitle: {
      margin: '0 0 10px 0',
      fontSize: '16px',
      color: '#111827',
      fontWeight: '600'
    },
    searchInputContainer: {
      position: 'relative',
      display: 'flex',
      gap: '8px'
    },
    searchInput: {
      flex: 1,
      padding: '10px 12px 10px 36px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      outline: 'none',
      transition: 'all 0.2s ease',
      '&:focus': {
        borderColor: '#3B82F6',
        boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
      }
    },
    searchIcon: {
      position: 'absolute',
      left: '12px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#6b7280',
      fontSize: '16px'
    },
    clearSearchButton: {
      background: '#ef4444',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      padding: '10px 16px',
      cursor: 'pointer',
      fontSize: '13px',
      fontWeight: '500',
      whiteSpace: 'nowrap',
      '&:hover': {
        backgroundColor: '#dc2626'
      }
    },
    searchResults: {
      maxHeight: '300px',
      overflowY: 'auto'
    },
    searchResultItem: (isSelected) => ({
      display: 'flex',
      alignItems: 'center',
      padding: '12px 15px',
      borderBottom: '1px solid #e5e7eb',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      backgroundColor: isSelected ? '#f0f9ff' : 'white',
      '&:hover': {
        backgroundColor: '#f8fafc'
      }
    }),
    searchResultNumber: (color) => ({
      width: '26px',
      height: '26px',
      borderRadius: '50%',
      backgroundColor: color,
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '12px',
      fontWeight: '700',
      marginRight: '12px',
      flexShrink: 0
    }),
    searchResultContent: {
      flex: 1,
      minWidth: 0
    },
    searchResultName: {
      margin: '0 0 4px 0',
      fontSize: '14px',
      fontWeight: '600',
      color: '#111827',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    searchResultAddress: {
      margin: 0,
      fontSize: '12px',
      color: '#6b7280',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    searchResultFE: {
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: '12px',
      backgroundColor: '#3B82F6',
      color: 'white',
      fontSize: '10px',
      fontWeight: '600',
      marginTop: '4px'
    },
    emptySearchResults: {
      padding: '30px 20px',
      textAlign: 'center',
      color: '#6b7280',
      fontSize: '14px'
    },
    // List Panel Styles
    listPanel: {
      position: 'absolute',
      top: '230px',
      right: '20px',
      background: 'white',
      borderRadius: '10px',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
      zIndex: 1000,
      minWidth: '300px',
      maxWidth: '400px',
      maxHeight: '400px',
      overflowY: 'auto',
      border: '1px solid #e5e7eb',
      display: showList ? 'flex' : 'none',
      flexDirection: 'column'
    },
    listHeader: {
      padding: '15px',
      borderBottom: '1px solid #e5e7eb',
      background: '#f8fafc',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    listTitle: {
      margin: 0,
      fontSize: '16px',
      color: '#111827',
      fontWeight: '600'
    },
    closeListButton: {
      background: 'none',
      border: 'none',
      fontSize: '20px',
      cursor: 'pointer',
      color: '#6b7280',
      lineHeight: 1,
      padding: '0',
      width: '24px',
      height: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '4px',
      '&:hover': {
        backgroundColor: '#f3f4f6'
      }
    },
    listContainer: {
      flex: 1,
      overflowY: 'auto'
    },
    listItem: (isSelected) => ({
      display: 'flex',
      alignItems: 'center',
      padding: '12px 15px',
      borderBottom: '1px solid #e5e7eb',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      backgroundColor: isSelected ? '#f0f9ff' : 'white',
      '&:hover': {
        backgroundColor: '#f8fafc'
      }
    }),
    listNumber: (color) => ({
      width: '26px',
      height: '26px',
      borderRadius: '50%',
      backgroundColor: color,
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '12px',
      fontWeight: '700',
      marginRight: '12px',
      flexShrink: 0
    }),
    listContent: {
      flex: 1,
      minWidth: 0
    },
    listPropertyName: {
      margin: '0 0 4px 0',
      fontSize: '14px',
      fontWeight: '600',
      color: '#111827',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    listPropertyAddress: {
      margin: 0,
      fontSize: '12px',
      color: '#6b7280',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    listBadges: {
      display: 'flex',
      gap: '6px',
      marginTop: '6px'
    },
    listBadge: (color) => ({
      padding: '2px 8px',
      borderRadius: '12px',
      backgroundColor: color,
      color: 'white',
      fontSize: '10px',
      fontWeight: '600',
      whiteSpace: 'nowrap'
    }),
    emptyList: {
      padding: '30px 20px',
      textAlign: 'center',
      color: '#6b7280',
      fontSize: '14px'
    },
    detailsPanel: {
      position: 'absolute',
      bottom: '20px',
      left: '20px',
      right: '20px',
      background: 'white',
      borderRadius: '10px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      zIndex: 1000,
      maxHeight: '450px',
      overflowY: 'auto',
      border: '1px solid #e5e7eb',
      transform: selectedProperty ? 'translateY(0)' : 'translateY(120%)',
      opacity: selectedProperty ? 1 : 0,
      transition: 'all 0.3s ease'
    },
    panelHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '12px 16px',
      borderBottom: '1px solid #e5e7eb',
      background: '#f8fafc'
    },
    panelTitle: {
      margin: 0,
      fontSize: '16px',
      color: '#111827',
      fontWeight: '600'
    },
    closeButton: {
      background: 'none',
      border: 'none',
      fontSize: '24px',
      cursor: 'pointer',
      color: '#6b7280',
      lineHeight: 1,
      padding: '0',
      width: '24px',
      height: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '4px',
      '&:hover': {
        backgroundColor: '#f3f4f6'
      }
    },
    panelContent: {
      padding: '16px'
    },
    badgeContainer: {
      display: 'flex',
      gap: '8px',
      marginBottom: '12px',
      flexWrap: 'wrap'
    },
    propertyTypeBadge: (color) => ({
      display: 'inline-block',
      padding: '3px 10px',
      borderRadius: '16px',
      backgroundColor: color,
      color: 'white',
      fontSize: '11px',
      fontWeight: '600'
    }),
    statusBadge: (color) => ({
      display: 'inline-block',
      padding: '3px 10px',
      borderRadius: '16px',
      backgroundColor: color,
      color: 'white',
      fontSize: '11px',
      fontWeight: '600'
    }),
    propertyInfo: {
      marginBottom: '16px'
    },
    propertyRow: {
      display: 'flex',
      marginBottom: '8px',
      fontSize: '14px'
    },
    propertyLabel: {
      fontWeight: '600',
      color: '#4b5563',
      marginRight: '8px',
      minWidth: '80px'
    },
    propertyValue: {
      color: '#6b7280',
      flex: 1
    },
    clickableValue: {
      color: '#3B82F6',
      cursor: 'pointer',
      textDecoration: 'underline',
      '&:hover': {
        color: '#2563eb'
      }
    },
    mediaSection: {
      marginTop: '16px',
      paddingTop: '16px',
      borderTop: '1px solid #e5e7eb'
    },
    mediaTitle: {
      margin: '0 0 12px 0',
      fontSize: '14px',
      color: '#374151',
      fontWeight: '600',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    mediaCount: {
      fontSize: '12px',
      color: '#6b7280',
      fontWeight: 'normal'
    },
    imageGallery: {
      display: 'flex',
      gap: '8px',
      marginBottom: '12px',
      overflowX: 'auto',
      paddingBottom: '8px'
    },
    thumbnail: {
      width: '60px',
      height: '60px',
      borderRadius: '6px',
      objectFit: 'cover',
      cursor: 'pointer',
      border: '2px solid transparent',
      transition: 'all 0.2s ease',
      '&:hover': {
        transform: 'scale(1.05)'
      }
    },
    activeThumbnail: {
      borderColor: '#3B82F6'
    },
    documentList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    },
    documentItem: {
      display: 'flex',
      alignItems: 'center',
      padding: '8px',
      background: '#f9fafb',
      borderRadius: '6px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      '&:hover': {
        background: '#f3f4f6',
        transform: 'translateX(2px)'
      }
    },
    documentIcon: {
      width: '20px',
      height: '20px',
      marginRight: '8px',
      color: '#6b7280'
    },
    documentName: {
      fontSize: '13px',
      color: '#4b5563',
      flex: 1
    },
    viewButton: {
      fontSize: '12px',
      color: '#3B82F6',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      fontWeight: '500'
    },
    actionButtons: {
      display: 'flex',
      gap: '8px',
      marginTop: '16px'
    },
    actionButton: (type) => ({
      padding: '8px 12px',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '13px',
      fontWeight: '500',
      transition: 'all 0.2s ease',
      backgroundColor: type === 'primary' ? '#3B82F6' : 
                     type === 'secondary' ? '#6b7280' : '#ef4444',
      color: 'white',
      flex: 1,
      '&:hover': {
        opacity: 0.9,
        transform: 'translateY(-1px)'
      }
    }),
    // Modal Styles
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.75)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 3000,
      padding: '20px'
    },
    imageModal: {
      position: 'relative',
      maxWidth: '90vw',
      maxHeight: '90vh',
      background: 'black'
    },
    modalImage: {
      maxWidth: '100%',
      maxHeight: '90vh',
      objectFit: 'contain'
    },
    modalNavButton: {
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      background: 'rgba(255, 255, 255, 0.2)',
      color: 'white',
      border: 'none',
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '20px',
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.3)'
      }
    },
    prevButton: {
      left: '20px'
    },
    nextButton: {
      right: '20px'
    },
    closeModalButton: {
      position: 'absolute',
      top: '20px',
      right: '20px',
      background: 'rgba(0, 0, 0, 0.5)',
      color: 'white',
      border: 'none',
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '24px',
      '&:hover': {
        background: 'rgba(0, 0, 0, 0.7)'
      }
    },
    imageCounter: {
      position: 'absolute',
      bottom: '20px',
      left: '50%',
      transform: 'translateX(-50%)',
      color: 'white',
      background: 'rgba(0, 0, 0, 0.5)',
      padding: '5px 15px',
      borderRadius: '20px',
      fontSize: '14px'
    },
    documentsModal: {
      background: 'white',
      borderRadius: '10px',
      padding: '20px',
      maxWidth: '500px',
      width: '90vw',
      maxHeight: '80vh',
      overflowY: 'auto'
    },
    documentsTitle: {
      margin: '0 0 20px 0',
      color: '#111827'
    }
  };

  // Show snackbar notification
  const showSnackbar = (message, severity = 'info') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Fetch properties from API
  const fetchProperties = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await propertyService.getAllProperties();

      const transformedData = data.map(property => ({
        id: property.id,
        name: property.name || 'Unnamed Property',
        type: property.type || '',
        status: property.status || '',
        latitude: parseFloat(property.latitude) || 0,
        longitude: parseFloat(property.longitude) || 0,
        coordinates: [parseFloat(property.latitude) || 0, parseFloat(property.longitude) || 0],
        address: property.address || '',
        details: property.details || '',
        contact: property.contact || '',
        photos: property.photos || [],
        documents: property.documents || [],
        fe_name: property.fe?.name_of_fe || ''
      }));
      
      // Filter out properties with invalid coordinates
      const validProperties = transformedData.filter(p => 
        p.coordinates[0] !== 0 && p.coordinates[1] !== 0
      );
      
      setProperties(validProperties);
      setFilteredProperties(validProperties);
      setFilteredBySearch(validProperties);
      
      // Show success notification
      showSnackbar(`Successfully loaded ${validProperties.length} properties`, 'success');
      
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Failed to load properties. Please try again.');
      showSnackbar('Failed to load properties. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  // Apply filter
  const applyFilter = (type) => {
    setFilter(type);
    
    if (type === 'all') {
      setFilteredProperties(properties);
      setShowList(false);
    } else {
      const filtered = properties.filter(p => p.type === type);
      setFilteredProperties(filtered);
      setShowList(true);
    }
    
    // Reset selected property when filter changes
    setSelectedProperty(null);
    setSelectedPropertyId(null);
    setSelectedPropertyIndex(null);
    setSearchQuery(''); // Clear search when filter changes
    setIsSearching(false);
  };

  // Handle search functionality
  const handleSearch = (query) => {
    setSearchQuery(query);
    setIsSearching(query.length > 0);
    
    if (query.trim() === '') {
      setFilteredBySearch(properties);
      setFilteredProperties(properties);
      setIsSearching(false);
      setSelectedProperty(null);
      setSelectedPropertyId(null);
      setSelectedPropertyIndex(null);
      return;
    }
    
    const searchTerm = query.toLowerCase().trim();
    const results = properties.filter(property => {
      const propertyName = property.name?.toLowerCase() || '';
      const feName = property.fe_name?.toLowerCase() || '';
      const address = property.address?.toLowerCase() || '';
      
      return (
        propertyName.includes(searchTerm) ||
        feName.includes(searchTerm) ||
        address.includes(searchTerm)
      );
    });
    
    setFilteredBySearch(results);
    setFilteredProperties(results);
    
    // Reset selection when searching - DON'T auto-select first result
    setSelectedProperty(null);
    setSelectedPropertyId(null);
    setSelectedPropertyIndex(null);
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
    setIsSearching(false);
    setFilteredBySearch(properties);
    setFilteredProperties(properties);
    setSelectedProperty(null);
    setSelectedPropertyId(null);
    setSelectedPropertyIndex(null);
    // Don't reset the filter here, keep current filter
    if (filter !== 'all') {
      const filtered = properties.filter(p => p.type === filter);
      setFilteredProperties(filtered);
    }
  };

  // Get user location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation([position.coords.latitude, position.coords.longitude]);
        },
        () => {
          console.log("Location permission denied");
        },
        { timeout: 10000 }
      );
    }
  }, []);

  // Create custom icon with numbering
  const createPropertyIcon = (type, index) => {
    const config = propertyTypes[type] || propertyTypes['3(2)'];
    return L.divIcon({
      html: `
        <div style="
          background-color: ${config.color};
          width: 28px;
          height: 28px;
          border-radius: 50%;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 12px;
          font-weight: bold;
          position: relative;
        ">
          ${index + 1}
        </div>
      `,
      className: 'custom-marker',
      iconSize: [30, 30],
      iconAnchor: [15, 15]
    });
  };

  // Open directions with satellite view
  const openDirections = (property) => {
    const [lat, lng] = property.coordinates;
    
    // Shows property with pin marker in satellite view
    const url = `https://www.google.com/maps/place/${lat},${lng}/@${lat},${lng},17z/data=!3m1!1e3?hl=en&entry=ttu&layer=s`;
    
    window.open(url, '_blank');
    showSnackbar('Opening property location in satellite view...', 'info');
  };

  // Handle property selection from list or map
  const handlePropertySelect = (property, index) => {
    setSelectedProperty(property);
    setSelectedPropertyId(property.id);
    setSelectedPropertyIndex(index);
    setSelectedImageIndex(0);
    
    // Scroll to the selected item in the list
    if (listRefs.current[index]) {
      listRefs.current[index].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
    
    // Center map on selected property
    if (mapRef.current && property.coordinates) {
      mapRef.current.setView(property.coordinates, 15, {
        animate: true,
        duration: 1
      });
    }
  };

  // Handle closing property details
  const handleCloseDetails = () => {
    setSelectedProperty(null);
    setSelectedPropertyId(null);
    setSelectedPropertyIndex(null);
  };

  // Handle closing list
  const handleCloseList = () => {
    setShowList(false);
    setFilter('all');
    setFilteredProperties(properties);
    setSelectedProperty(null);
    setSelectedPropertyId(null);
    setSelectedPropertyIndex(null);
  };

  // Image gallery navigation
  const nextImage = () => {
    if (selectedProperty && selectedProperty.photos) {
      setSelectedImageIndex((prev) => 
        prev === selectedProperty.photos.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (selectedProperty && selectedProperty.photos) {
      setSelectedImageIndex((prev) => 
        prev === 0 ? selectedProperty.photos.length - 1 : prev - 1
      );
    }
  };

  // Get file icon based on extension
  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    switch (ext) {
      case 'pdf': return '📄';
      case 'doc': case 'docx': return '📝';
      case 'xls': case 'xlsx': return '📊';
      case 'jpg': case 'jpeg': case 'png': case 'gif': return '🖼️';
      default: return '📎';
    }
  };

  // Get file name from URL
  const getFileNameFromUrl = (url) => {
    return url.split('/').pop().split('?')[0];
  };

  // Initialize
  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  // Show loading spinner during initial load
  if (loading && properties.length === 0) {
    return (
      <div style={styles.container}>
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 2000
        }}>
          <LoadingSpinner size={40} />
          <p style={{ textAlign: 'center', marginTop: '16px', color: '#4b5563' }}>
            Loading properties...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.headerTitle}>GeoProperty Asset Intelligence Suite</h1>
        <p style={styles.headerSubtitle}>
          Interactive map showing all properties with filtering and detailed views
        </p>
      </div>

      {/* Filter Panel */}
      <div style={styles.filterPanel}>
        <h3 style={styles.filterTitle}>Property Types</h3>
        <div style={styles.filterButtons}>
          <button 
            style={styles.filterButton(filter === 'all', '#1e40af')}
            onClick={() => applyFilter('all')}
          >
            All Properties ({properties.length})
          </button>
          {Object.entries(propertyTypes).map(([type, config]) => (
            <button
              key={type}
              style={styles.filterButton(filter === type, config.color)}
              onClick={() => applyFilter(type)}
            >
              {config.label} ({properties.filter(p => p.type === type).length})
            </button>
          ))}
        </div>

        {/* Legend */}
        <div style={styles.legend}>
          <h4 style={styles.legendTitle}>Legend</h4>
          {Object.entries(propertyTypes).map(([type, config]) => (
            <div key={type} style={styles.legendItem}>
              <div style={styles.legendColor(config.color)}></div>
              <span>{config.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Search Bar */}
      <div style={styles.searchContainer}>
        <div style={styles.searchHeader}>
          <h3 style={styles.searchTitle}>Search Properties</h3>
          <div style={styles.searchInputContainer}>
            <div style={styles.searchIcon}>🔍</div>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search by FE Name, Property Name, or Address..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              style={styles.searchInput}
            />
            {searchQuery && (
              <button 
                style={styles.clearSearchButton}
                onClick={clearSearch}
              >
                Clear
              </button>
            )}
          </div>
        </div>
        
        {/* Search Results */}
        {isSearching && filteredBySearch.length > 0 && (
          <div style={styles.searchResults}>
            {filteredBySearch.map((property, index) => (
              <div
                key={property.id}
                ref={el => listRefs.current[index] = el}
                style={styles.searchResultItem(selectedPropertyId === property.id)}
                onClick={() => handlePropertySelect(property, index)}
              >
                <div style={styles.searchResultNumber(propertyTypes[property.type]?.color || '#3B82F6')}>
                  {index + 1}
                </div>
                <div style={styles.searchResultContent}>
                  <h4 style={styles.searchResultName}>
                    {property.name}
                  </h4>
                  <p style={styles.searchResultAddress}>
                    {property.address || 'No address available'}
                  </p>
                  {property.fe_name && (
                    <div style={styles.searchResultFE}>
                      FE: {property.fe_name}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Empty Search Results */}
        {isSearching && filteredBySearch.length === 0 && (
          <div style={styles.emptySearchResults}>
            No properties found matching "{searchQuery}"
          </div>
        )}
      </div>

      {/* Property List Panel (Only shows when specific type is selected) */}
      {showList && filter !== 'all' && !isSearching && (
        <div style={styles.listPanel}>
          <div style={styles.listHeader}>
            <h3 style={styles.listTitle}>
              {propertyTypes[filter]?.label || filter} Properties ({filteredProperties.length})
            </h3>
            <button 
              style={styles.closeListButton}
              onClick={handleCloseList}
            >
              ×
            </button>
          </div>
          
          <div style={styles.listContainer}>
            {filteredProperties.length === 0 ? (
              <div style={styles.emptyList}>
                No {propertyTypes[filter]?.label || filter} properties found.
              </div>
            ) : (
              filteredProperties.map((property, index) => (
                <div
                  key={property.id}
                  ref={el => listRefs.current[index] = el}
                  style={styles.listItem(selectedPropertyId === property.id)}
                  onClick={() => handlePropertySelect(property, index)}
                >
                  <div style={styles.listNumber(propertyTypes[property.type]?.color || '#3B82F6')}>
                    {index + 1}
                  </div>
                  <div style={styles.listContent}>
                    <h4 style={styles.listPropertyName}>
                      {property.name}
                    </h4>
                    <p style={styles.listPropertyAddress}>
                      {property.address || 'No address available'}
                    </p>
                    <div style={styles.listBadges}>
                      <div style={styles.listBadge(statusConfig[property.status]?.color || '#6b7280')}>
                        {property.status || 'Unknown'}
                      </div>
                      {property.fe_name && (
                        <div style={styles.listBadge('#3B82F6')}>
                          FE: {property.fe_name}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Map with Copyright-Compliant Layers - HYBRID DEFAULT CHECKED */}
      <MapContainer
        center={[20.5937, 78.9629]}
        zoom={5}
        style={styles.mapContainer}
        scrollWheelZoom={true}
        whenCreated={(map) => {
          mapRef.current = map;
        }}
      >
        <LayersControl position="topright">
          
          {/* Esri Satellite Layer - Copyright Compliant */}
          <BaseLayer checked name='Satellite'>
            <TileLayer
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              attribution='Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
            />
          </BaseLayer>
          
          {/* OpenStreetMap Layer - Copyright Compliant */}
          <BaseLayer name="Street Map">
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
          </BaseLayer>
          
          {/* Alternative Hybrid - OSM Labels over Satellite */}
          {/* <BaseLayer name="OSM Hybrid">
            <TileLayer
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              attribution='Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
            />
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              opacity={0.3}
            />
          </BaseLayer> */}
          
          {/* CartoDB Dark Matter - Alternative stylish map */}
          {/* <BaseLayer name="Dark Theme">
            <TileLayer
              url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
            />
          </BaseLayer> */}
          
          {/* Stamen Terrain - Copyright Compliant */}
          {/* <BaseLayer name="Terrain">
            <TileLayer
              url="https://stamen-tiles-{s}.a.ssl.fastly.net/terrain/{z}/{x}/{y}.jpg"
              attribution='Map tiles by <a href="http://stamen.com">Stamen Design</a>, under <a href="http://creativecommons.org/licenses/by/3.0">CC BY 3.0</a>. Data by <a href="http://openstreetmap.org">OpenStreetMap</a>, under <a href="http://www.openstreetmap.org/copyright">ODbL</a>.'
            />
          </BaseLayer> */}
        </LayersControl>

        {/* User Location */}
        {userLocation && (
          <Marker position={userLocation}>
            <Popup>
              <div style={{ fontSize: '14px' }}>
                <strong>Your Location</strong>
              </div>
            </Popup>
          </Marker>
        )}

        {/* Property Markers with Numbering */}
        {filteredProperties.map((property, index) => (
          <Marker
            key={property.id}
            position={property.coordinates}
            icon={createPropertyIcon(property.type, index)}
            eventHandlers={{
              click: () => handlePropertySelect(property, index)
            }}
          >
            <Popup>
              <div style={{ minWidth: '280px', fontSize: '14px' }}>
                <h4 style={{ margin: '0 0 8px 0', color: '#111827' }}>
                  #{index + 1} - {property.name}
                </h4>
                <div style={{
                  display: 'flex',
                  gap: '6px',
                  marginBottom: '8px',
                  flexWrap: 'wrap'
                }}>
                  <div style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    backgroundColor: propertyTypes[property.type]?.color || '#3B82F6',
                    color: 'white',
                    fontSize: '11px',
                    fontWeight: '600'
                  }}>
                    {propertyTypes[property.type]?.label || property.type}
                  </div>
                  <div style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    backgroundColor: statusConfig[property.status]?.color || '#6b7280',
                    color: 'white',
                    fontSize: '11px',
                    fontWeight: '600'
                  }}>
                    {property.status || 'Unknown'}
                  </div>
                </div>
                {property.fe_name && (
                  <p style={{ margin: '4px 0' }}>
                    <strong>FE:</strong> {property.fe_name}
                  </p>
                )}
                <p style={{ margin: '4px 0' }}>
                  <strong>Address:</strong> {property.address || 'Not specified'}
                </p>
                {property.contact && (
                  <p style={{ margin: '4px 0' }}>
                    <strong>Contact:</strong> {property.contact}
                  </p>
                )}
                {property.photos && property.photos.length > 0 && (
                  <p style={{ margin: '4px 0', fontSize: '12px', color: '#6b7280' }}>
                    📸 {property.photos.length} image{property.photos.length !== 1 ? 's' : ''}
                  </p>
                )}
                {property.documents && property.documents.length > 0 && (
                  <p style={{ margin: '4px 0', fontSize: '12px', color: '#6b7280' }}>
                    📎 {property.documents.length} document{property.documents.length !== 1 ? 's' : ''}
                  </p>
                )}
                <div style={{ marginTop: '12px' }}>
                  <button
                    onClick={() => openDirections(property)}
                    style={{
                      padding: '6px 12px',
                      background: '#10B981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      width: '100%'
                    }}
                  >
                    Get Directions
                  </button>
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      {/* Property Details Panel */}
      <div style={styles.detailsPanel}>
        {selectedProperty && (
          <>
            <div style={styles.panelHeader}>
              <h3 style={styles.panelTitle}>
                #{selectedPropertyIndex !== null ? selectedPropertyIndex + 1 : ''} - {selectedProperty.name}
              </h3>
              <button 
                style={styles.closeButton}
                onClick={handleCloseDetails}
              >
                ×
              </button>
            </div>
            <div style={styles.panelContent}>
              {/* Property Type and Status Badges */}
              <div style={styles.badgeContainer}>
                <div style={styles.propertyTypeBadge(propertyTypes[selectedProperty.type]?.color || '#3B82F6')}>
                  {propertyTypes[selectedProperty.type]?.label || selectedProperty.type}
                </div>
                <div style={styles.statusBadge(statusConfig[selectedProperty.status]?.color || '#6b7280')}>
                  {selectedProperty.status || 'Unknown'}
                </div>
              </div>
              
              {/* Property Details */}
              <div style={styles.propertyInfo}>
                {selectedProperty.fe_name && (
                  <div style={styles.propertyRow}>
                    <span style={styles.propertyLabel}>FE Name:</span>
                    <span style={styles.propertyValue}>{selectedProperty.fe_name}</span>
                  </div>
                )}
                <div style={styles.propertyRow}>
                  <span style={styles.propertyLabel}>Address:</span>
                    <span style={styles.propertyValue}>{selectedProperty.address || 'Not specified'}</span>
                </div>
                {selectedProperty.details && (
                  <div style={styles.propertyRow}>
                    <span style={styles.propertyLabel}>Details:</span>
                    <span style={styles.propertyValue}>{selectedProperty.details}</span>
                  </div>
                )}
                {selectedProperty.contact && (
                  <div style={styles.propertyRow}>
                    <span style={styles.propertyLabel}>Contact:</span>
                    <span style={{...styles.propertyValue, ...styles.clickableValue}} 
                          onClick={() => {
                            window.open(`tel:${selectedProperty.contact}`);
                            showSnackbar(`Calling ${selectedProperty.contact}`, 'info');
                          }}>
                      {selectedProperty.contact}
                    </span>
                  </div>
                )}
                <div style={styles.propertyRow}>
                  <span style={styles.propertyLabel}>Coordinates:</span>
                  <span style={styles.propertyValue}>
                    {selectedProperty.latitude.toFixed(6)}, {selectedProperty.longitude.toFixed(6)}
                  </span>
                </div>
              </div>

              {/* Images Section */}
              {selectedProperty.photos && selectedProperty.photos.length > 0 && (
                <div style={styles.mediaSection}>
                  <h4 style={styles.mediaTitle}>
                    Images
                    <span style={styles.mediaCount}>
                      {selectedProperty.photos.length} image{selectedProperty.photos.length !== 1 ? 's' : ''}
                    </span>
                  </h4>
                  <div style={styles.imageGallery}>
                    {selectedProperty.photos.map((img, index) => (
                      <img
                        key={index}
                        src={img}
                        alt={`Property ${index + 1}`}
                        style={{
                          ...styles.thumbnail,
                          ...(selectedImageIndex === index && styles.activeThumbnail)
                        }}
                        onClick={() => {
                          setSelectedImageIndex(index);
                          setShowImageGallery(true);
                        }}
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/60x60?text=No+Image';
                        }}
                      />
                    ))}
                  </div>
                  <button
                    onClick={() => setShowImageGallery(true)}
                    style={{
                      padding: '6px 12px',
                      background: '#3B82F6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      width: '100%'
                    }}
                  >
                    View All Images
                  </button>
                </div>
              )}

              {/* Documents Section */}
              {selectedProperty.documents && selectedProperty.documents.length > 0 && (
                <div style={styles.mediaSection}>
                  <h4 style={styles.mediaTitle}>
                    Documents
                    <span style={styles.mediaCount}>
                      {selectedProperty.documents.length} document{selectedProperty.documents.length !== 1 ? 's' : ''}
                    </span>
                  </h4>
                  <div style={styles.documentList}>
                    {selectedProperty.documents.map((doc, index) => (
                      <div key={index} style={styles.documentItem}>
                        <span style={styles.documentIcon}>{getFileIcon(doc)}</span>
                        <span style={styles.documentName}>
                          {getFileNameFromUrl(doc)}
                        </span>
                        <button
                          onClick={() => {
                            window.open(doc, '_blank');
                            showSnackbar('Opening document in new tab...', 'info');
                          }}
                          style={styles.viewButton}
                        >
                          View
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => setShowDocumentsModal(true)}
                    style={{
                      padding: '6px 12px',
                      background: '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      width: '100%',
                      marginTop: '8px'
                    }}
                  >
                    View All Documents
                  </button>
                </div>
              )}

              {/* Directions Section */}
              <div style={{ ...styles.mediaSection, borderTop: '1px solid #e5e7eb', paddingTop: '16px', marginTop: '16px' }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#374151', fontWeight: '600' }}>
                  Get Directions
                </h4>
                <div style={styles.actionButtons}>
                  <button 
                    style={styles.actionButton('primary')}
                    onClick={() => openDirections(selectedProperty)}
                  >
                    Open Directions
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Image Gallery Modal */}
      {showImageGallery && selectedProperty && selectedProperty.photos && (
        <div style={styles.modalOverlay} onClick={() => setShowImageGallery(false)}>
          <div style={styles.imageModal} onClick={(e) => e.stopPropagation()}>
            <button
              style={{ ...styles.closeModalButton }}
              onClick={() => setShowImageGallery(false)}
            >
              ×
            </button>
            
            <img
              src={selectedProperty.photos[selectedImageIndex]}
              alt={`Property image ${selectedImageIndex + 1}`}
              style={styles.modalImage}
              onError={(e) => {
                e.target.src = 'https://via.placeholder.com/800x600?text=Image+Not+Available';
              }}
            />
            
            {selectedProperty.photos.length > 1 && (
              <>
                <button
                  style={{ ...styles.modalNavButton, ...styles.prevButton }}
                  onClick={prevImage}
                >
                  ‹
                </button>
                <button
                  style={{ ...styles.modalNavButton, ...styles.nextButton }}
                  onClick={nextImage}
                >
                  ›
                </button>
                <div style={styles.imageCounter}>
                  {selectedImageIndex + 1} / {selectedProperty.photos.length}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Documents Modal */}
      {showDocumentsModal && selectedProperty && (
        <div style={styles.modalOverlay} onClick={() => setShowDocumentsModal(false)}>
          <div style={styles.documentsModal} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.documentsTitle}>
              Documents for {selectedProperty.name}
            </h3>
            <div style={styles.documentList}>
              {selectedProperty.documents.map((doc, index) => (
                <div key={index} style={styles.documentItem}>
                  <span style={styles.documentIcon}>{getFileIcon(doc)}</span>
                  <span style={styles.documentName}>
                    {getFileNameFromUrl(doc)}
                  </span>
                  <button
                    onClick={() => {
                      window.open(doc, '_blank');
                      showSnackbar('Opening document in new tab...', 'info');
                    }}
                    style={styles.viewButton}
                  >
                    Open
                  </button>
                  <button
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = doc;
                      link.download = getFileNameFromUrl(doc);
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      showSnackbar('Downloading document...', 'success');
                    }}
                    style={{
                      ...styles.viewButton,
                      marginLeft: '8px',
                      color: '#10B981'
                    }}
                  >
                    Download
                  </button>
                </div>
              ))}
            </div>
            <button
              onClick={() => setShowDocumentsModal(false)}
              style={{
                marginTop: '20px',
                padding: '8px 16px',
                background: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                width: '100%'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Snackbar Component */}
      <SnackBarComponent
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
      />
    </div>
  );
};

export default PropertyMap;